/**
 * When the interesting part of a track actually happens.
 *
 * Loggers are switched on at home and off in the evening, so the recorded span
 * is nothing like the flying. On the 10-pilot group file the chart spanned
 * 04:00-16:32 (12.5 h) while the flying was 11:24-14:57. `computeContentWindow`
 * returns the part worth showing — measured 10:00-15:45 = 5.8 h — and
 * `LineChart` unions it over the *visible* flights to set the time axis and the
 * Cesium clock.
 *
 * ## Why this doesn't classify what the pilot is doing
 *
 * The obvious approach — decide per fix whether this is flying, hiking, driving
 * or idling — was tried and measured, and neither speed nor the altitude trace
 * separates a car from a flight:
 *
 *   dahm, driving a mountain road   13 km/h   1.0 vario sign changes/min
 *   stumpf, flying                  36 km/h   0.5 vario sign changes/min
 *
 * The drive looks *busier* than the flight. Serpentines are slower than a glide,
 * and GPS altitude wobbles on a road. The only signal that truly separates the
 * two is height above terrain, which would mean a terrain dependency here.
 *
 * ## What works instead
 *
 * Three cheap structural facts, none of which needs to know about cars:
 *
 * 1. **The gaps.** Loggers are turned OFF between the drive and the flying, and
 *    those holes are unmistakable — 127 and 128 minutes against a 1 s fix rate.
 *    Splitting on them separates sessions that are already semantically
 *    different: `bernie-hertz 07:06-08:28` (82 min, altitude range 4 m, sat at
 *    home), `dahm 04:00-05:29` (parked), `dahm 05:42-07:34` (the drive).
 * 2. **Sustained activity, not samples.** Runs are closed across dropouts up to
 *    `CLOSE_SEC` and then have to last `MIN_RUN_SEC`. Both halves are
 *    load-bearing and in this order: a stationary logger of michelle's drifted
 *    75 m per minute, so isolated fixes kept clearing the movement threshold and
 *    her window ran 90 minutes past her landing — but measuring un-closed runs
 *    cut the last 6 minutes off giorgio's descent, because a single weak minute
 *    split it and the remainder was too short to keep.
 * 3. **Blocks, split by long pauses.** Activity separated by more than
 *    `MAX_PAUSE_SEC` is a different part of the day. Only blocks containing real
 *    flying are kept, and the window spans the first to the last of them. That
 *    drops the drive home without any test for driving: measured, those blocks
 *    contain zero flying time (dahm 15:42-16:00, jana 15:42-16:03, michelle
 *    15:31-15:45). An altitude-range test on top changes nothing on this data,
 *    so there isn't one.
 *
 * Activity itself is three signals ORed together — horizontal speed over 30 s,
 * displacement over 10 minutes, and vertical rate over 60 s. All three are
 * needed: a spiral descent defeats both horizontal tests at once, and a
 * stationary logger's drift defeats a short horizontal window.
 *
 * **Hikes survive by construction** — what gets dropped is time below walking
 * pace, and a walk-up is not below walking pace. That is why the run test is a
 * *movement* test rather than a flying test, and why a file with no flying at
 * all falls back to its activity rather than being thrown away.
 *
 * ## The known limit
 *
 * A hike that ends more than `MAX_PAUSE_SEC` before takeoff — a long wait at
 * launch — becomes its own block, contains no flying, and is dropped. Raising
 * the pause drags the drive home back in (measured: at 60 min the union grows
 * by 45 min), so 30 minutes is the compromise. If this ever bites, the fix is
 * to keep a non-flying block when it is *adjacent* to a kept one, not to widen
 * the pause.
 */

const GAP_SEC = 600; // a hole this long means the logger was off → new session

// Flying test. The 30 s window is load-bearing: at 3 minutes a thermalling
// pilot's displacement averages away to nothing and two of the ten pilots
// stopped being recognised as flying at all.
const FLY_WINDOW_SEC = 30;
const FLY_KMH = 25;
const FLY_MIN_SEC = 180;

// Movement test. Ten minutes, not the three it started as: a stationary logger
// can drift far enough to clear 1.5 km/h over three minutes, and the noise only
// cancels out over a long enough window. A hike covers ~500 m in ten minutes,
// so the separation is wide.
const MOVE_WINDOW_SEC = 600;
const MOVE_KMH = 1.5;

// Vertical motion, the third way of being busy. A pilot spiralling down moves
// almost nowhere horizontally — giorgio lost 400 m of height over 450 m of
// ground — so both speed tests fail at once and the descent was being cut off
// the end of the flight. Measured |vertical rate| over 60 s:
//
//   standing (3 pilots, incl. the noisiest logger)   p50 0.05-0.15   max 0.76
//   spiral descent                                   p50 0.97        p90 1.54
//   normal flight                                    p50 1.12        p90 1.86
//
// 0.8 m/s sits above every stationary sample measured and below the spiral's
// median. It only has to hold for MIN_RUN_SEC, so the odd noise spike is
// harmless.
const VARIO_WINDOW_SEC = 60;
const VARIO_MS = 0.8;

// Bridge momentary dropouts BEFORE measuring how long a run lasted. All three
// activity tests can dip below threshold for a moment in the middle of real
// activity — measured on giorgio's final approach, one minute at fly 11.2,
// move 1.2, vario 0.25 split the run and left the last 6 minutes of his descent
// as a 4.5-minute fragment, which MIN_RUN_SEC then discarded. His landing was
// cut off the chart.
//
// It has to be generous, because a *soaring* flight dips below all three
// thresholds constantly — slow, level, turning. Swept against the group file:
//
//   0 s / 30 s   giorgio's landing is cut (window ends 15:00, he lands 15:06)
//   60 s         landing kept, but his FIRST flight is lost: 10:02-10:52
//                ridge soaring at 2130-2400 m fragments into runs too short to
//                survive MIN_RUN_SEC, so the chart opened with his curve
//                already airborne at 2300 m
//   120 s        both kept, and nothing false gained — the drives home still
//                measure zero flying time and are still dropped
//
// A short soaring flight is easy to mistake for a drive, and I did: 261 m of
// altitude range looks like a road until you notice it oscillates, sits at
// 2240 m, and has 20 minutes of standing still on either side. Check that
// before concluding a block is traffic.
const CLOSE_SEC = 120;

const MIN_RUN_SEC = 300; // shorter bursts of "activity" are noise
const MAX_PAUSE_SEC = 1800; // a longer pause separates one part of the day from the next

const R = 6371000;
const TO_RAD = Math.PI / 180;

function metres(a, b) {
  const p1 = a.lat * TO_RAD;
  const p2 = b.lat * TO_RAD;
  const dp = p2 - p1;
  const dl = (b.lng - a.lng) * TO_RAD;
  const h =
    Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Straight-line speed (km/h) from each fix to the one `windowSec` later.
 *
 * Displacement between the window's ends, NOT distance travelled along the
 * track — that is what makes circling read as slow and a glide as fast, and it
 * is why the window length matters so much. The `j` pointer only ever moves
 * forward, so this is O(n) rather than O(n·window): 4 ms for 86k fixes.
 */
function windowSpeeds(data, windowSec) {
  const n = data.length;
  const speeds = new Float32Array(n);
  let j = 0;
  for (let i = 0; i < n; i++) {
    if (j < i) j = i;
    while (j + 1 < n && data[j + 1].epoch - data[i].epoch < windowSec) j++;
    const dt = data[j].epoch - data[i].epoch;
    speeds[i] = dt > 0 ? (metres(data[i], data[j]) / dt) * 3.6 : 0;
  }
  return speeds;
}

/** |vertical rate| (m/s) from each fix to the one `windowSec` later. */
function windowVario(data, windowSec) {
  const n = data.length;
  const rates = new Float32Array(n);
  let j = 0;
  for (let i = 0; i < n; i++) {
    if (j < i) j = i;
    while (j + 1 < n && data[j + 1].epoch - data[i].epoch < windowSec) j++;
    const dt = data[j].epoch - data[i].epoch;
    rates[i] = dt > 0 ? Math.abs(data[j].altitude - data[i].altitude) / dt : 0;
  }
  return rates;
}

/** Index ranges [from, to) between which the logger was off. */
function splitSessions(data) {
  const sessions = [];
  let start = 0;
  for (let i = 0; i < data.length - 1; i++) {
    if (data[i + 1].epoch - data[i].epoch > GAP_SEC) {
      sessions.push([start, i + 1]);
      start = i + 1;
    }
  }
  sessions.push([start, data.length]);
  return sessions;
}

/** Blocks of sustained activity, with how much flying each one contains. */
function activityBlocks(data, fly, move, vario) {
  const blocks = [];
  for (const [from, to] of splitSessions(data)) {
    // Every stretch of activity, however short…
    const raw = [];
    let runStart = -1;
    for (let i = from; i < to; i++) {
      const active =
        fly[i] >= FLY_KMH || move[i] >= MOVE_KMH || vario[i] >= VARIO_MS;
      if (active && runStart < 0) runStart = i;
      if (!active && runStart >= 0) {
        raw.push([runStart, i - 1]);
        runStart = -1;
      }
    }
    if (runStart >= 0) raw.push([runStart, to - 1]);

    // …closed across momentary dropouts, and only then measured. Doing it the
    // other way round throws away the tail of a real activity whenever one
    // sample in the middle dips below threshold.
    const closed = [];
    for (const run of raw) {
      const last = closed[closed.length - 1];
      if (last && data[run[0]].epoch - data[last[1]].epoch <= CLOSE_SEC) last[1] = run[1];
      else closed.push(run.slice());
    }
    const runs = closed.filter(
      ([a, b]) => data[b].epoch - data[a].epoch >= MIN_RUN_SEC
    );

    for (const run of runs) {
      const last = blocks[blocks.length - 1];
      const sameBlock =
        last &&
        last.session === from &&
        data[run[0]].epoch - data[last.to].epoch <= MAX_PAUSE_SEC;
      if (sameBlock) last.to = run[1];
      else blocks.push({ session: from, from: run[0], to: run[1] });
    }
  }

  for (const block of blocks) {
    let flying = 0;
    for (let i = block.from; i < block.to; i++) {
      if (fly[i] >= FLY_KMH) flying += data[i + 1].epoch - data[i].epoch;
    }
    block.flying = flying;
  }
  return blocks;
}

/**
 * `{ start, end }` epochs of the part worth showing, or null when the track is
 * too short to say anything. Callers fall back to the full recorded span.
 */
export function computeContentWindow(data) {
  if (!data || data.length < 2) return null;

  const fly = windowSpeeds(data, FLY_WINDOW_SEC);
  const move = windowSpeeds(data, MOVE_WINDOW_SEC);
  const vario = windowVario(data, VARIO_WINDOW_SEC);
  const blocks = activityBlocks(data, fly, move, vario);
  if (blocks.length === 0) return null;

  // Blocks with real flying define the window. With none — a pure walk-up, or a
  // track cut short — every block of activity does, which still drops the
  // parked hours instead of throwing the file away.
  const flown = blocks.filter((b) => b.flying >= FLY_MIN_SEC);
  const use = flown.length > 0 ? flown : blocks;

  let start = Infinity;
  let end = -Infinity;
  for (const block of use) {
    start = Math.min(start, data[block.from].epoch);
    end = Math.max(end, data[block.to].epoch);
  }
  return end > start ? { start, end } : null;
}
